import type { ProgramOptions } from "ogl";
import { Vec3, Program } from "ogl";
import type { Engine3D } from "@ogl-engine/engine/engine3d";
import type { Engine2D } from "@ogl-engine/engine/engine2d";
import { colorToVec3 } from "@core/utils/maps";
import fragment from "./shader.frag.glsl";
import fragment2d from "./shader2d.frag.glsl";
import vertex from "./shader.vert.glsl";
import vertex2d from "./shader2d.vert.glsl";
import type { Uniforms } from "../material";
import { Material, Material2D } from "../material";

export class ColorMaterial extends Material {
  uniforms: Material["uniforms"] &
    Uniforms<{
      uColor: Vec3;
      fEmissive: number;
      bShaded: boolean;
    }>;

  constructor(
    engine: Engine3D,
    opts: {
      color?: string;
      shaded?: boolean;
      layer?: string;
    } & Partial<Omit<ProgramOptions, "vertex" | "fragment" | "uniforms">> = {}
  ) {
    super(engine);

    const defines: Record<string, string> = {};

    this.createProgram(vertex, fragment, defines, {
      cullFace: false,
      ...opts,
    });

    this.uniforms.uColor = { value: new Vec3() };
    this.uniforms.fEmissive = { value: 0 };
    this.uniforms.bShaded = { value: opts.shaded ?? true };

    if (opts.color) {
      this.setColor(opts.color);
    }
  }

  setColor(color: string) {
    colorToVec3(color, this.uniforms.uColor.value);
  }

  getColor(): Vec3 {
    return this.uniforms.uColor.value;
  }
}

export class ColorMaterial2D extends Material2D {
  uniforms: Material["uniforms"] &
    Uniforms<{
      uColor: Vec3;
    }>;

  constructor(engine: Engine2D, color: string) {
    super(engine);

    this.program = new Program(engine.gl, {
      vertex: vertex2d,
      fragment: fragment2d,
      uniforms: this.uniforms,
      cullFace: false,
    });
    this.uniforms.uColor = { value: new Vec3() };

    this.setColor(color);
  }

  setColor(color: string) {
    colorToVec3(color, this.uniforms.uColor.value);
  }
}
